import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import useGroupLabels from 'hooks/useGroupLabels';
import { formatGroupCell } from 'utils/groupLabel';
import style from './AthleteAttendanceTable.module.css';

// Column structure for athlete attendance
const columns = [
  { key: 'firstName', label: 'First Name', centered: false },
  { key: 'lastName', label: 'Last Name', centered: false },
  // Headed Age or Year by the club's grouping, where the table is drawn.
  { key: 'age', centered: true },
  { key: 'gender', label: 'Gender', centered: true },
  { key: 'meetsAttended', label: 'Meets', centered: true },
  { key: 'attendancePercentage', label: 'Attendance %', centered: true },
];

const AthleteAttendanceTable = ({
  athleteAttendance,
  setSearchText,
  minAttendancePercent,
  setMinAttendancePercent,
  totalMeets,
}) => {
  // The athlete's group column reads Age and "11", or Year and "Y6" for a club that groups by year level.
  const { groupingBasis, words } = useGroupLabels();
  const [athleteAttendanceTableData, setAthleteAttendanceTableData] = useState([]);

  const [sortKeys, setSortKeys] = useState(
    columns.reduce((acc, col) => {
      acc[col.key] = false;
      return acc;
    }, {})
  );

  useEffect(() => {
    // Sort by attendance percentage (highest first) by default
    const sortedData = [...athleteAttendance].sort((a, b) =>
      b.attendancePercentage - a.attendancePercentage
    );
    setAthleteAttendanceTableData(sortedData);
    // Reset sort keys so attendancePercentage shows as descending
    setSortKeys(
      columns.reduce((acc, col) => {
        acc[col.key] = col.key === 'attendancePercentage'; // true = descending for attendance
        return acc;
      }, {})
    );
  }, [athleteAttendance]);

  const handleAthleteSearch = (e) => {
    setSearchText(e.target.value);
  };

  const handleMinAttendanceChange = (e) => {
    const value = parseInt(e.target.value, 10);
    setMinAttendancePercent(isNaN(value) || value < 0 ? 0 : value);
  };

  const sortTable = (key) => {
    // Create a copy to avoid mutating state directly
    const sortData = [...athleteAttendanceTableData].sort((a, b) => {
      if (['firstName', 'lastName', 'gender'].includes(key)) {
        // String comparison
        const aVal = (a[key] || '').toString().toLowerCase();
        const bVal = (b[key] || '').toString().toLowerCase();
        return sortKeys[key]
          ? bVal.localeCompare(aVal)
          : aVal.localeCompare(bVal);
      } else {
        // Numeric comparison
        return sortKeys[key] ? b[key] - a[key] : a[key] - b[key];
      }
    });

    setAthleteAttendanceTableData(sortData);

    // Toggle sort direction
    setSortKeys({
      ...sortKeys,
      [key]: !sortKeys[key],
    });
  };

  // Generate attendance percentage options (0, 10, 20, ..., 100)
  const attendanceOptions = [];
  for (let i = 0; i <= 100; i += 10) {
    attendanceOptions.push(i);
  }

  return (
    <div className={style.tableCard}>
      {/* Header with Season info */}
      <div className={style.tableHeader}>
        <div className={style.seasonInfo}>
          {totalMeets} {totalMeets === 1 ? 'meet' : 'meets'} that {totalMeets === 1 ? 'counts' : 'count'} for season points this season
        </div>
      </div>

      {/* What the numbers mean */}
      <p className={style.description}>
        Attendance is measured against the meets that count for season points. An athlete is counted as
        attending a meet once they have at least one result recorded at it, and Attendance % is meets
        attended out of the {totalMeets} {totalMeets === 1 ? 'meet' : 'meets'} above.
      </p>

      {/* Search Bar */}
      <div className={style.searchBar}>
        <input
          type="text"
          className={style.searchInput}
          placeholder="Search athletes by name..."
          id="athleteSearch"
          onInput={handleAthleteSearch}
        />
        <div className={style.filterGroup}>
          <label htmlFor="minAttendancePercent" className={style.filterLabel}>
            Min Attendance %:
          </label>
          <select
            id="minAttendancePercent"
            className={style.selectInput}
            value={minAttendancePercent}
            onChange={handleMinAttendanceChange}
          >
            {attendanceOptions.map((value) => (
              <option key={value} value={value}>
                {value}%
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Table */}
      <div className={style.tableWrapper}>
        <table className={style.dataTable}>
          <thead>
            <tr>
              {columns.map((col, index) => (
                <th
                  key={index}
                  onClick={() => sortTable(col.key)}
                  className={col.centered ? style.centered : ''}
                >
                  {col.key === 'age' ? words.value : col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {athleteAttendanceTableData.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length}
                  style={{
                    textAlign: 'center',
                    padding: '40px',
                    color: '#6c757d',
                  }}
                >
                  No athlete attendance data available
                </td>
              </tr>
            ) : (
              athleteAttendanceTableData.map((athlete, index) => (
                <tr key={athlete.athleteID || index}>
                  <td className={style.athleteName}>{athlete.firstName}</td>
                  <td className={style.athleteName}>{athlete.lastName}</td>
                  <td className={style.centered}>{formatGroupCell(groupingBasis, athlete.age)}</td>
                  <td className={style.centered}>{athlete.gender}</td>
                  <td className={style.centered}>{athlete.meetsAttended}</td>
                  <td className={style.attendancePercent}>{athlete.attendancePercentage}%</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

AthleteAttendanceTable.propTypes = {
  athleteAttendance: PropTypes.array,
  setSearchText: PropTypes.func,
  minAttendancePercent: PropTypes.number,
  setMinAttendancePercent: PropTypes.func,
  totalMeets: PropTypes.number,
};

export default AthleteAttendanceTable;
